// Row → domain mapping.
//
// Two impedance mismatches to bridge, in one place so no call site has to
// think about either:
//
//   snake_case  →  camelCase
//   timestamptz →  epoch milliseconds
//
// The epoch-ms conversion is deliberate. The domain types in types/domain.ts
// use `number` throughout and the whole UI formats from that, so converting at
// the boundary keeps the entire component tree unchanged by this migration.
//
// BIGINT arrives from PostgREST as a JS number. Rupiah amounts are far below
// 2^53, so that is lossless here — but it is why money columns must never
// become NUMERIC with decimals, which PostgREST would hand back as a string.

import type { Database } from '@/types/database';
import type {
  AppEvent,
  AppTransaction,
  AppUser,
  Category,
  Customer,
  EventStockAllocation,
  Product,
  ReceivablePayment,
  TransactionItem,
  AppliedPromo,
} from '@/types/domain';

type Tables = Database['public']['Tables'];

/** timestamptz → epoch ms. Null-safe: absent timestamps stay undefined. */
export const ts = (v: string | null | undefined): number | undefined =>
  v ? new Date(v).getTime() : undefined;

/** Same, but for columns that are NOT NULL, so callers get a plain number. */
export const tsReq = (v: string): number => new Date(v).getTime();

/** epoch ms → ISO string for writes. */
export const toIso = (v: number | null | undefined): string | null =>
  v == null ? null : new Date(v).toISOString();

export function mapProfile(r: Tables['profiles']['Row']): AppUser {
  return {
    uid: r.id,
    email: r.email,
    displayName: r.display_name,
    role: r.role,
    activeEventId: r.active_event_id ?? undefined,
    createdAt: tsReq(r.created_at),
  };
}

export function mapEvent(r: Tables['events']['Row']): AppEvent {
  return {
    id: r.id,
    name: r.name,
    code: r.code,
    status: r.status,
    location: r.location ?? undefined,
    color: r.color ?? undefined,
    startAt: tsReq(r.start_at),
    endAt: ts(r.end_at),
    totalSales: r.total_sales,
    totalTransactions: r.total_transactions,
    createdBy: r.created_by ?? '',
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

export function mapCategory(r: Tables['categories']['Row']): Category {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? undefined,
    sortOrder: r.sort_order,
    isActive: r.is_active,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

export function mapProduct(r: Tables['products']['Row']): Product {
  return {
    id: r.id,
    sku: r.sku,
    name: r.name,
    price: r.price,
    costPrice: r.cost_price ?? undefined,
    // Products created before the categories table have their category as
    // free text; the UI merges both, so surface whichever is set.
    categoryId: r.category_id ?? r.legacy_category ?? undefined,
    imageUrl: r.image_url ?? undefined,
    isActive: r.is_active,
    unit: r.unit ?? 'pcs',
    brand: r.brand ?? undefined,
    description: r.description ?? undefined,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

export function mapCustomer(r: Tables['customers']['Row']): Customer {
  return {
    id: r.id,
    name: r.name,
    company: r.company,
    phone: r.phone,
    taxId: r.tax_id ?? undefined,
    address: r.address ?? undefined,
    email: r.email,
    isActive: r.is_active,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

export function mapAllocation(
  r: Tables['event_stock_allocations']['Row'],
): EventStockAllocation {
  return {
    id: r.id,
    productId: r.product_id,
    productSku: r.product_sku,
    productName: r.product_name,
    productPrice: r.product_price,
    qtyIn: r.qty_in,
    qtySold: r.qty_sold,
    qtyReturnedToGudang: r.qty_returned_to_gudang,
    lotNumber: r.lot_number ?? '',
    expiryDate: r.expiry_date ? new Date(r.expiry_date).getTime() : undefined,
    unit: r.unit ?? 'pcs',
    orderForm: r.order_form ?? '',
    createdBy: r.created_by ?? '',
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
    closedAt: ts(r.closed_at),
    closedBy: r.closed_by ?? undefined,
  };
}

export function mapPayment(r: Tables['payments']['Row']): ReceivablePayment {
  return {
    id: r.id,
    transactionId: r.transaction_id,
    customerId: r.customer_id ?? '',
    eventId: r.event_id ?? '',
    amount: r.amount,
    paymentMethod: r.payment_method,
    paidAt: tsReq(r.paid_at),
    receivedBy: r.received_by ?? '',
    receivedByName: r.received_by_name ?? undefined,
    notes: r.notes ?? undefined,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

type TxRow = Tables['transactions']['Row'] & {
  transaction_items?: Tables['transaction_items']['Row'][] | null;
  /** Present when selecting from transactions_view. */
  effective_payment_status?: AppTransaction['paymentStatus'] | null;
};

function mapAppliedPromos(raw: unknown): AppliedPromo[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((a) => {
    if (!a || typeof a !== 'object') return [];
    const o = a as Record<string, unknown>;
    if (typeof o.promo_id !== 'string' || typeof o.kind !== 'string') return [];
    return [{
      promoId: o.promo_id,
      kind: o.kind as AppliedPromo['kind'],
      name: typeof o.name === 'string' ? o.name : '',
      amount: typeof o.amount === 'number' ? o.amount : Number(o.amount ?? 0),
      times: typeof o.times === 'number' ? o.times : undefined,
      free: typeof o.free === 'number' ? o.free : undefined,
      steps: Array.isArray(o.steps) ? o.steps.map(Number) : undefined,
    }];
  });
}

export function mapTransactionItem(
  r: Tables['transaction_items']['Row'],
): TransactionItem {
  return {
    productId: r.product_id ?? '',
    allocationId: r.allocation_id ?? undefined,
    name: r.name,
    price: r.price,
    qty: r.qty,
    returnedQty: r.returned_qty,
    subtotal: r.subtotal,
    lotNumber: r.lot_number ?? undefined,
    expiryDate: r.expiry_date ? new Date(r.expiry_date).getTime() : undefined,
    unit: r.unit ?? undefined,
    promoDiscount: r.promo_discount ?? 0,
    promoId: r.promo_id ?? undefined,
  };
}

export function mapTransaction(r: TxRow): AppTransaction {
  return {
    id: r.id,
    invoiceNumber: r.invoice_number,
    eventId: r.event_id,
    customerId: r.customer_id ?? undefined,
    customerSnapshot:
      (r.customer_snapshot as AppTransaction['customerSnapshot']) ?? undefined,
    items: (r.transaction_items ?? []).map(mapTransactionItem),
    subtotal: r.subtotal,
    discountType: r.discount_type,
    discountValue: r.discount_value,
    discountAmount: r.discount_amount,
    taxEnabled: r.tax_enabled,
    taxRate: Number(r.tax_rate),
    taxAmount: r.tax_amount,
    promoDiscount: r.promo_discount ?? 0,
    appliedPromos: mapAppliedPromos(r.applied_promos),
    grandTotal: r.grand_total,
    paymentMethod: r.payment_method,
    // Prefer the view's live value: 'overdue' is computed from the due date at
    // read time, so the stored column can legitimately lag behind it.
    paymentStatus: r.effective_payment_status ?? r.payment_status,
    fulfillment: r.fulfillment,
    fulfilledAt: ts(r.fulfilled_at),
    changeAmount: r.change_amount,
    cashierId: r.cashier_id ?? '',
    cashierName: r.cashier_name ?? undefined,
    notes: r.notes ?? undefined,
    voidedAt: ts(r.voided_at),
    voidedByName: r.voided_by_name ?? undefined,
    voidReason: r.void_reason ?? undefined,
    voidApprovedByName: r.void_approved_by_name ?? undefined,
    paymentTerms: r.payment_terms,
    dueDate: ts(r.due_date),
    paidAmount: r.paid_amount,
    outstandingAmount: r.outstanding_amount,
    pdfUrl: r.pdf_url ?? undefined,
    pdfGeneratedAt: ts(r.pdf_generated_at),
    pdfGenerationError: r.pdf_error ?? undefined,
    // wa_* replaced the openclaw_* names, which predated n8n/Hermes/Cloud API
    // and really meant "whichever gateway ran". Domain field names are kept so
    // the existing components don't change.
    openclawStatus: r.wa_status ?? undefined,
    openclawAttempts: r.wa_attempts,
    openclawLastError: r.wa_last_error ?? undefined,
    openclawSentAt: ts(r.wa_sent_at),
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

/** Columns needed to hydrate a full AppTransaction, including its lines. */
export const TX_SELECT = '*, transaction_items(*)';
